import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient as api } from '../../services/apiClient';
import { GateType } from '../../types/writing';
import './WritingHistoryDetailPage.css';

const GATE_CONFIG: Record<GateType, { nameKey: string; icon: string }> = {
  translation: { nameKey: 'translateToEnglish', icon: '📋' },
  combine: { nameKey: 'sentenceCombining', icon: '🔗' },
  expansion: { nameKey: 'paragraphExpansion', icon: '📝' },
  essay: { nameKey: 'essayWriting', icon: '✍️' },
};

interface GateResult {
  gateNumber: number;
  gateType: string;
  questions: any[];
  answers: any[];
  score: number;
  isPassed: boolean;
  aiFeedback: {
    overallScore: number;
    strengths?: string[];
    improvements?: string[];
    correctedVersion?: string;
    encouragement?: string;
  } | null;
  completedAt: string | null;
}

interface SessionDetail {
  id: string;
  userId: string;
  courseId: string | null;
  type: string;
  status: string;
  scenarioContext: any;
  generatedQuestions: Record<string, any[]> | null;
  currentGate: number;
  totalScore: number;
  pointsEarned: number;
  tokensConsumed: number;
  timeSpentSeconds: number;
  completedAt: string | null;
  createdAt: string;
  gateResults: GateResult[];
}

const WritingHistoryDetailPage: React.FC = () => {
  const { t } = useTranslation(['writing', 'common']);
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;

    const loadSession = async () => {
      setLoading(true);
      try {
        // Fetch session details from history
        const response = await api.get(`/writing/history`);
        const historyData = response.data?.data || response.data;
        const quests = historyData?.quests || [];
        const found = quests.find((q: any) => q.id === sessionId);
        if (found) {
          setSession(found);
        } else {
          setError(t('writing:historyDetailNotFound'));
        }
      } catch (err: any) {
        setError(err.message || t('common:failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId, t]);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#16a34a';
    if (score >= 60) return '#ca8a04';
    return '#dc2626';
  };

  const gateNumberToType = (n: number): GateType => {
    const map: Record<number, GateType> = { 1: 'translation', 2: 'combine', 3: 'expansion', 4: 'essay' };
    return map[n] || 'translation';
  };

  const renderGateResult = (result: GateResult) => {
    const gt = (result.gateType || gateNumberToType(result.gateNumber)) as GateType;
    const config = GATE_CONFIG[gt];
    const feedback = result.aiFeedback;

    return (
      <div key={result.gateNumber} className={`gate-result ${result.isPassed ? 'passed' : 'failed'}`}>
        <div className="gate-result-header">
          <span className="gate-icon">{config?.icon}</span>
          <span className="gate-name">{config ? t(`writing:${config.nameKey}`) : gt}</span>
          <span className="gate-score" style={{ color: getScoreColor(result.score) }}>
            {result.score}
          </span>
          <span className={`gate-status ${result.isPassed ? 'pass' : 'fail'}`}>
            {result.isPassed ? t('writing:passed') : t('writing:gateFailed')}
          </span>
        </div>

        {feedback && (
          <div className="gate-feedback">
            {feedback.strengths && feedback.strengths.length > 0 && (
              <div className="feedback-section strengths">
                <h4>{t('writing:strengths')}</h4>
                <ul>
                  {feedback.strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {feedback.improvements && feedback.improvements.length > 0 && (
              <div className="feedback-section improvements">
                <h4>{t('writing:areasToImprove')}</h4>
                <ul>
                  {feedback.improvements.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {feedback.correctedVersion && (
              <div className="feedback-section corrected">
                <h4>{t('writing:gateFeedbackImproved')}</h4>
                <p className="corrected-text">{feedback.correctedVersion}</p>
              </div>
            )}
            {feedback.encouragement && (
              <p className="encouragement">{feedback.encouragement}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="quest-history-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('writing:historyDetailLoading')}</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="quest-history-detail">
        <div className="error-container">
          <p>{error || t('writing:historyDetailNotFound')}</p>
          <Link to="/writing/history">{t('writing:historyDetailBack')}</Link>
        </div>
      </div>
    );
  }

  const gateResults = session.gateResults || [];
  const avgScore =
    gateResults.length > 0
      ? Math.round(gateResults.reduce((a, r) => a + r.score, 0) / gateResults.length)
      : 0;

  return (
    <div className="quest-history-detail">
      <div className="page-header">
        <Link to="/writing/history" className="back-link">{t('writing:historyDetailBack')}</Link>
        <h1>{t('writing:historyDetailTitle')}</h1>
      </div>

      <div className="session-summary">
        <div className="summary-score" style={{ color: getScoreColor(avgScore) }}>
          {avgScore}
        </div>
        <div className="summary-info">
          <p>{formatDate(session.completedAt || session.createdAt)}</p>
          <p>{t('writing:xp')}: {session.pointsEarned} | {t('writing:tokensLabel')}: {session.tokensConsumed}</p>
          <p className="session-type-label">
            {session.type === 'standalone' ? t('writing:personalPractice') : t('writing:courseQuest')}
          </p>
        </div>
      </div>

      <div className="gate-results">
        {gateResults
          .sort((a, b) => a.gateNumber - b.gateNumber)
          .map(renderGateResult)}
      </div>

      {gateResults.length === 0 && (
        <div className="no-results">
          <p>{t('writing:noGateResults')}</p>
        </div>
      )}
    </div>
  );
};

export default WritingHistoryDetailPage;
